'use client';

import { Github } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { triggerConfetti } from '@/lib/confetti';
import { useAuth } from '@/components/auth/AuthProvider';
import { useRef } from 'react';

interface GithubSignInButtonProps {
  variant?: 'header' | 'hero' | 'faq' | 'icon';
  className?: string;
}

export function GithubSignInButton({ 
  variant = 'header',
  className,
}: GithubSignInButtonProps) {
  const { signInWithGitHub } = useAuth();
  const buttonRef = useRef<HTMLButtonElement>(null);

  const variants = {
    header: 'h-9 px-4',
    hero: 'h-11 px-8 min-w-[180px]',
    faq: 'h-11 px-8 min-w-[200px]',
    icon: 'h-9 w-9 px-0 bg-transparent hover:bg-transparent text-foreground'
  };

  const buttonClassName = cn(
    'inline-flex items-center justify-center whitespace-nowrap rounded-md text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50',
    {
      'bg-white text-black hover:bg-gray-100 border-0': variant !== 'icon'
    },
    variants[variant],
    className
  );

  const handleSignIn = async () => {
    try {
      await signInWithGitHub();
    } catch (error) {
      console.error('Error signing in:', error);
    }
  };

  const handleMouseEnter = () => {
    triggerConfetti({});
  };

  return (
    <Button
      ref={buttonRef}
      onClick={handleSignIn}
      onMouseEnter={handleMouseEnter}
      className={buttonClassName}
    >
      <Github className={cn("h-4 w-4", { "mr-4": variant !== 'icon' })} />
      {variant !== 'icon' && "Sign in with GitHub"}
    </Button>
  );
}
